import { isEmpty } from '../../../util/array';
import { isEmptySet, isNil } from '../../../util/object';
import { TEMPLATE_FILTER_CATEGORY_DEFINITIONS } from './constants';
import {
  ACTIVE_GROUP_TEMPLATE_CATEGORY,
  GLOBAL_ID,
  INACTIVE_GROUP_TEMPLATE_CATEGORY,
  SORT_BY_OPTIONS,
} from '../../../constants';
import Tag from '../../../model/tag/Tag';

export const filterTemplatesByOwnership = (
  {
    globalActiveTemplates,
    globalInactiveTemplates,
    inactiveTemplatesForGroup,
    activeTemplatesForGroup,
  },
  ownershipSet,
) => {
  const isUnfiltered = ownershipSet.size === 0;
  const templates = [];

  if (
    !isEmpty(globalActiveTemplates) &&
    (isUnfiltered ||
      ownershipSet.has(
        TEMPLATE_FILTER_CATEGORY_DEFINITIONS.ACCOUNT.GLOBAL_ACTIVE_TEMPLATES.id,
      ))
  ) {
    templates.push(...globalActiveTemplates);
  }
  if (
    !isEmpty(globalInactiveTemplates) &&
    (isUnfiltered ||
      ownershipSet.has(
        TEMPLATE_FILTER_CATEGORY_DEFINITIONS.ACCOUNT.GLOBAL_INACTIVE_TEMPLATES
          .id,
      ))
  ) {
    templates.push(...globalInactiveTemplates);
  }
  if (
    !isEmpty(activeTemplatesForGroup) &&
    (isUnfiltered ||
      ownershipSet.has(
        TEMPLATE_FILTER_CATEGORY_DEFINITIONS.ACCOUNT.YOUR_ACTIVE_TEMPLATES.id,
      ))
  ) {
    templates.push(...activeTemplatesForGroup);
  }
  if (
    !isEmpty(inactiveTemplatesForGroup) &&
    (isUnfiltered ||
      ownershipSet.has(
        TEMPLATE_FILTER_CATEGORY_DEFINITIONS.ACCOUNT.YOUR_INACTIVE_TEMPLATES.id,
      ))
  ) {
    templates.push(...inactiveTemplatesForGroup);
  }

  return templates;
};

export const hasTagFilters = (
  templateTags,
  selectedTagTypesArray,
  selectedTagsByType,
) => {
  // must have at least one of filters per type
  for (let i = 0; i < selectedTagTypesArray.length; i++) {
    const selectedTagType = selectedTagTypesArray[i];
    const selectedTags = selectedTagsByType[selectedTagType]; // array of selected tag ids
    const templateSatisfiesSelectedTagsForType = selectedTags.some(
      (selectedTag) => {
        return templateTags.includes(selectedTag);
      },
    );
    if (!templateSatisfiesSelectedTagsForType) {
      // template does not satisfy the filters and should not be displayed
      return false;
    }
  }
  return true;
};

export const groupDesignsByStatus = (list) => {
  if (!list) {
    return list;
  }

  const groups = {};
  list.forEach((templateInList) => {
    const { active } = templateInList;
    const category = active
      ? ACTIVE_GROUP_TEMPLATE_CATEGORY.defaultMessage
      : INACTIVE_GROUP_TEMPLATE_CATEGORY.defaultMessage;

    if (!groups[category]) {
      groups[category] = {
        key: category,
        label: category,
        templates: [],
      };
    }

    groups[category].templates.push(templateInList);
  });

  const groupKeys = Object.keys(groups).sort();
  const groupedTemplates = [];
  groupKeys.forEach((key) => {
    groupedTemplates.push(groups[key]);
  });

  return groupedTemplates;
};

export const filterDesignsByTags = (
  templates,
  selectedFilters,
  allFiltersById,
) => {
  if (isEmptySet(selectedFilters) || isEmpty(templates)) return templates;

  // group the selected filters by their tag type
  const selectedTagsByType = {};
  const selectedTagTypes = new Set();
  selectedFilters.forEach((selectedFilter) => {
    const tag = allFiltersById[selectedFilter];
    if (Tag.isTag(tag)) {
      if (!selectedTagTypes.has(tag.type)) {
        selectedTagsByType[tag.type] = [];
        selectedTagTypes.add(tag.type);
      }
      selectedTagsByType[tag.type].push(tag.id);
    }
  });

  return templates.filter(({ tags }) => {
    if (isEmpty(tags)) return false;

    const templateTags = tags.map((tag) => tag.id);
    const selectedTagTypesArray = [...selectedTagTypes];
    // check that template has satisfied the selected filters
    return hasTagFilters(
      templateTags,
      selectedTagTypesArray,
      selectedTagsByType,
    );
  });
};

export const sortTemplates = (templates, sortBy) => {
  if (isNil(templates)) return [];

  const sortedTemplates = [...templates];
  sortedTemplates.sort((a, b) => {
    // active templates go to the top
    const isActiveA = a.active;
    const isActiveB = b.active;
    if (isActiveA && !isActiveB) {
      return -1;
    } else if (!isActiveA && isActiveB) {
      return 1;
    }

    // the ordering should go: Active Account Templates, Active Global, Inactive Account, Inactive Global
    // Account (non-global) templates go to the top
    const isGlobalA =
      a.publisherId === GLOBAL_ID || a.organizationId === GLOBAL_ID;
    const isGlobalB =
      b.publisherId === GLOBAL_ID || b.organizationId === GLOBAL_ID;
    if (isGlobalA && !isGlobalB) {
      return 1;
    } else if (!isGlobalA && isGlobalB) {
      return -1;
    }

    // use selected sortBy
    if (sortBy === SORT_BY_OPTIONS.NAME) {
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    } else {
      if (isGlobalA || isGlobalB) {
        // default to ordinal (last modified does not apply for global)
        // higher ordinals go to the top
        const ordinalA = +a.ordinal || -1;
        const ordinalB = +b.ordinal || -1;
        return ordinalA === ordinalB ? 0 : ordinalB - ordinalA;
      } else {
        // default to last updated/modified
        const updatedAtA = Date.parse(a.updatedAt) || -1;
        const updatedAtB = Date.parse(b.updatedAt) || -1;
        return updatedAtA === updatedAtB ? 0 : updatedAtB - updatedAtA;
      }
    }
  });
  return sortedTemplates;
};
